import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';
import { numberAsKr } from '../Formatters';

export interface PriceField {
  label: string;
  current: number;
}

interface Props {
  product: string;
  fields: PriceField[];
  /** Saves the new prices, in the order of {@link fields}. A rejection is shown in the dialog. */
  onSave(prices: number[], timeOfChange: Moment): Promise<unknown>;
  onClose(): void;
}

export function PriceChangeDialog({ product, fields, onSave, onClose }: Props) {
  const [prices, setPrices] = useState(fields.map((x) => String(x.current)));
  // A product without a price yet has nothing to keep until a later day.
  const [fromNow, setFromNow] = useState(fields.every((x) => x.current === 0));
  const [day, setDay] = useState<Moment | null>(moment().add(1, 'year').startOf('year'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tomorrow = moment().add(1, 'day').startOf('day');
  const parsed = prices.map((x) => (x.trim() === '' ? NaN : Number(x)));
  const pricesValid = parsed.every((x) => Number.isFinite(x) && x >= 0);
  const changed = parsed.some((x, i) => x !== fields[i].current);
  const dayValid = fromNow || (!!day?.isValid() && !day.isBefore(tomorrow));

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await onSave(parsed, fromNow ? moment() : day!.clone().startOf('day'));
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Kunne ikke lagre prisendringen.');
      setSaving(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>Endre pris – {product}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {fields.map((field, i) => (
              <TextField
                key={field.label}
                label={field.label}
                type="number"
                value={prices[i]}
                onChange={(e) => setPrices(prices.map((x, j) => (j === i ? e.target.value : x)))}
                error={!Number.isFinite(parsed[i]) || parsed[i] < 0}
                helperText={currentAndChange(field.current, parsed[i])}
                required
                fullWidth
                autoFocus={i === 0}
                slotProps={{ input: { startAdornment: <InputAdornment position="start">kr</InputAdornment> }, htmlInput: { min: 0 } }}
              />
            ))}
          </Stack>
          <Stack spacing={1.5}>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              value={fromNow ? 'now' : 'day'}
              onChange={(_, value) => value && setFromNow(value === 'now')}
              aria-label="Gjelder fra"
            >
              <ToggleButton value="day">Fra dato</ToggleButton>
              <ToggleButton value="now">Fra nå</ToggleButton>
            </ToggleButtonGroup>
            {fromNow ? (
              <Alert severity="warning">Prisen endres med en gang du lagrer.</Alert>
            ) : (
              <DatePicker
                label="Gjelder fra"
                value={day}
                onChange={setDay}
                minDate={tomorrow}
                slotProps={{ textField: { fullWidth: true, required: true, helperText: 'Fram til da gjelder dagens pris.' } }}
              />
            )}
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={onClose}>
          Avbryt
        </Button>
        <Button variant="contained" disabled={!pricesValid || !changed || !dayValid} loading={saving} onClick={save}>
          {fromNow ? 'Endre pris nå' : 'Lagre prisendring'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function currentAndChange(current: number, next: number) {
  const now = `Nå ${numberAsKr(current) ?? '0 kr'}`;
  if (!Number.isFinite(next) || next === current || current === 0) return now;
  const percent = ((next - current) / current) * 100;
  const sign = percent > 0 ? '+' : '−';
  return `${now} · ${sign}${Math.abs(percent).toLocaleString('nb-NO', { maximumFractionDigits: 1 })} %`;
}
